import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Label } from '../ui/label';
import { toast } from 'sonner';
import { TrendingUp, TrendingDown, IndianRupee, Users, AlertCircle, Download, MinusCircle } from 'lucide-react';
import { SummaryCard } from './SummaryCard';
import { formatCurrency, formatMonth } from './utils';
import { exportToCSV } from '../../utils/csvExport';
import { getTodayString, dateToMonthString } from '../../utils/dateUtils';

const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/api\/?$/, '');
const API = `${BACKEND_URL}/api`;

export function EmployeeSalaryReport() {
  const [employees, setEmployees] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState('all');
  const [salaryData, setSalaryData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchEmployees(); }, []);
  useEffect(() => { fetchSalaryReport(); }, [selectedEmployee, employees]);

  const fetchEmployees = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/employees`, { headers: { Authorization: `Bearer ${token}` } });
      setEmployees(response.data);
    } catch (error) { 
      toast.error('Failed to load employees'); 
    } finally {
      setLoading(false);
    }
  };

  const fetchSalaryReport = async () => {
    if (employees.length === 0) {
      setSalaryData([]);
      return;
    }
    
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      const months = [];
      const now = new Date();
      // Start from previous month (i=1) — current month salary not yet paid
      for (let i = 1; i < 12; i++) {
        const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
        months.push(dateToMonthString(d));
      }
      
      const employeesToFetch = selectedEmployee === 'all' ? employees : employees.filter(e => e.employee_id === selectedEmployee);
      
      const getEmployeeJoinMonth = (emp) => {
        const dateStr = emp.start_date || emp.created_at;
        if (dateStr) {
          const d = new Date(dateStr);
          return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
        }
        return null;
      };
      
      let earliestJoinMonth = null;
      if (selectedEmployee !== 'all') {
        const emp = employeesToFetch[0];
        earliestJoinMonth = getEmployeeJoinMonth(emp);
      } else {
        for (const emp of employeesToFetch) {
          const joinMonth = getEmployeeJoinMonth(emp);
          if (joinMonth && (!earliestJoinMonth || joinMonth < earliestJoinMonth)) {
            earliestJoinMonth = joinMonth;
          }
        }
      }
      
      // Build all API calls upfront
      const apiCalls = [];
      const callMeta = []; // Track which month/employee each call is for
      
      for (const month of months) {
        if (earliestJoinMonth && month < earliestJoinMonth) {
          continue;
        }
        
        for (const emp of employeesToFetch) {
          const empJoinMonth = getEmployeeJoinMonth(emp);
          if (empJoinMonth && month < empJoinMonth) {
            continue;
          }
          
          apiCalls.push(
            axios.get(`${API}/employees/${emp.employee_id}/salary?month=${month}`, { headers })
              .then(res => ({ success: true, data: res.data }))
              .catch(() => ({ success: false, fallback: emp.monthly_salary || 0 }))
          );
          callMeta.push({ month, emp });
        }
      }
      
      // Execute all API calls in parallel (batched for performance)
      const BATCH_SIZE = 20;
      const results = [];
      for (let i = 0; i < apiCalls.length; i += BATCH_SIZE) {
        const batch = apiCalls.slice(i, i + BATCH_SIZE);
        const batchResults = await Promise.all(batch);
        results.push(...batchResults);
      }
      
      // Group results by month
      const monthMap = new Map();
      
      results.forEach((result, index) => {
        const { month, emp } = callMeta[index];
        
        if (!monthMap.has(month)) {
          monthMap.set(month, { 
            month, 
            employees: [], 
            totalSalary: 0, 
            totalAdvance: 0, 
            totalDeduction: 0,
            totalOvertime: 0, 
            totalNet: 0 
          });
        }
        
        const monthData = monthMap.get(month);
        
        if (result.success) {
          const data = result.data;
          monthData.employees.push({
            name: emp.name,
            baseSalary: data.base_salary,
            advance: data.available_advance,
            deduction: data.deduction || 0,
            overtime: data.overtime_payment,
            net: data.net_payment
          });
          monthData.totalSalary += data.base_salary;
          monthData.totalAdvance += data.available_advance;
          monthData.totalDeduction += (data.deduction || 0);
          monthData.totalOvertime += data.overtime_payment;
          monthData.totalNet += data.net_payment;
        } else {
          monthData.employees.push({
            name: emp.name,
            baseSalary: result.fallback,
            advance: 0,
            deduction: 0,
            overtime: 0,
            net: result.fallback
          });
          monthData.totalSalary += result.fallback;
          monthData.totalNet += result.fallback;
        }
      });
      
      // Convert map to array, sorted by month descending
      const reportData = Array.from(monthMap.values())
        .filter(m => m.employees.length > 0)
        .sort((a, b) => b.month.localeCompare(a.month));
      
      setSalaryData(reportData);
    } catch (error) { 
      toast.error('Failed to load salary report'); 
    } finally { 
      setLoading(false); 
    }
  };

  const totals = salaryData.reduce((acc, month) => ({
    salary: acc.salary + month.totalSalary,
    advance: acc.advance + month.totalAdvance,
    deduction: acc.deduction + month.totalDeduction,
    overtime: acc.overtime + month.totalOvertime,
    net: acc.net + month.totalNet
  }), { salary: 0, advance: 0, deduction: 0, overtime: 0, net: 0 });

  const handleExportCSV = () => {
    if (salaryData.length === 0) {
      toast.error('No salary data to export');
      return;
    }
    
    const columns = [
      { key: 'month', label: 'Month' },
      { key: 'totalAdvance', label: 'Total Advance (₹)' },
      { key: 'totalDeduction', label: 'Total Deduction (₹)' },
      { key: 'totalOvertime', label: 'Total Overtime (₹)' },
      { key: 'totalNet', label: 'Net Payment (₹)' }
    ];
    
    const formattedData = salaryData.map(row => ({
      ...row,
      month: formatMonth(row.month)
    }));
    
    const employeeName = selectedEmployee === 'all' ? 'all_employees' : employees.find(e => e.employee_id === selectedEmployee)?.name || 'unknown';
    const filename = `salary_report_${employeeName.replace(/\s/g, '_')}_${getTodayString()}`;
    exportToCSV(formattedData, columns, filename);
    toast.success('Salary report exported to CSV');
  };

  if (!loading && employees.length === 0) {
    return (
      <div className="space-y-6">
        <div>
          <h3 className="text-lg font-bold text-slate-900">Employee Salary Report</h3>
          <p className="text-sm text-slate-500">Monthly breakdown of salaries, advances and overtime</p>
        </div>
        <Card className="p-12 text-center border-0 rounded-2xl shadow-lg bg-white">
          <AlertCircle className="w-16 h-16 text-amber-400 mx-auto mb-4" />
          <p className="text-xl font-semibold text-slate-700 mb-2">No Employees Available</p>
          <p className="text-slate-500">Please add employees in the Employee Management tab first.</p>
        </Card>
      </div>
    );
  }

  if (loading && employees.length === 0) return <div className="flex items-center justify-center h-32"><div className="w-6 h-6 border-2 border-slate-300 border-t-amber-500 rounded-full animate-spin" /></div>;

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h3 className="text-lg font-bold text-slate-900">Employee Salary Report</h3>
          <p className="text-sm text-slate-500">Monthly breakdown of salaries, advances and overtime</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Label className="text-sm">Employee:</Label>
          <Select value={selectedEmployee} onValueChange={setSelectedEmployee}>
            <SelectTrigger className="w-48 h-10 rounded-lg">
              <SelectValue placeholder="Select employee" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Employees</SelectItem>
              {employees.map(emp => (
                <SelectItem key={emp.employee_id} value={emp.employee_id}>{emp.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            data-testid="export-salary-report-csv"
            variant="outline"
            onClick={handleExportCSV}
            disabled={salaryData.length === 0}
            className="rounded-xl h-9 w-9 p-0"
            title="Export CSV"
          >
            <Download className="w-4 h-4" />
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <SummaryCard title="Total Advance" amount={totals.advance} icon={TrendingDown} gradient="bg-gradient-to-br from-amber-500 to-amber-600" />
        <SummaryCard title="Total Deduction" amount={totals.deduction} icon={MinusCircle} gradient="bg-gradient-to-br from-red-500 to-red-600" />
        <SummaryCard title="Total Overtime" amount={totals.overtime} icon={TrendingUp} gradient="bg-gradient-to-br from-blue-500 to-blue-600" />
        <SummaryCard title="Net Payment" amount={totals.net} icon={IndianRupee} gradient="bg-gradient-to-br from-slate-800 to-slate-900" />
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-32"><div className="w-6 h-6 border-2 border-slate-300 border-t-amber-500 rounded-full animate-spin" /></div>
      ) : salaryData.length === 0 ? (
        <Card className="p-12 text-center border-0 rounded-2xl shadow-lg bg-white">
          <Users className="w-12 h-12 text-slate-300 mx-auto mb-4" />
          <p className="text-slate-600">No salary data available for this period</p>
        </Card>
      ) : (
        <Card className="border-0 rounded-2xl shadow-lg bg-white overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-4 py-3 text-left font-semibold text-slate-700">Month</th>
                  {selectedEmployee === 'all' ? (
                    <>
                      <th className="px-4 py-3 text-right font-semibold text-slate-700">Total Advance</th>
                      <th className="px-4 py-3 text-right font-semibold text-slate-700">Total Deduction</th>
                      <th className="px-4 py-3 text-right font-semibold text-slate-700">Total Overtime</th>
                      <th className="px-4 py-3 text-right font-semibold text-slate-700">Net Payment</th>
                    </>
                  ) : (
                    <>
                      <th className="px-4 py-3 text-right font-semibold text-slate-700">Advance</th>
                      <th className="px-4 py-3 text-right font-semibold text-slate-700">Deduction</th>
                      <th className="px-4 py-3 text-right font-semibold text-slate-700">Overtime</th>
                      <th className="px-4 py-3 text-right font-semibold text-slate-700">Net Payment</th>
                    </>
                  )}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {salaryData.map((monthData, i) => (
                  <tr key={i} className="hover:bg-slate-50">
                    <td className="px-4 py-3 font-medium text-slate-900">{formatMonth(monthData.month)}</td>
                    <td className="px-4 py-3 text-right text-amber-600">{formatCurrency(monthData.totalAdvance)}</td>
                    <td className="px-4 py-3 text-right text-red-600">{formatCurrency(monthData.totalDeduction)}</td>
                    <td className="px-4 py-3 text-right text-blue-600">{formatCurrency(monthData.totalOvertime)}</td>
                    <td className="px-4 py-3 text-right font-bold text-slate-900">{formatCurrency(monthData.totalNet)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
}
